// src/engine/printPdf.js
//
// Minimal print-and-cut PDF writer (no dependencies).
//
// Output: one page sized to the sticker + bleed (true millimetres), with
//   1. the artwork as a lossless RGB image + alpha soft-mask (transparent
//      background, so unprinted vinyl stays unprinted), and
//   2. the cut line as a vector path stroked in a Separation (spot)
//      colour named "CutContour" (magenta alternate, overprint on) —
//      the convention Roland VersaWorks and most print-and-cut RIPs use
//      to generate the contour-cut automatically.
//
// Compression uses the browser's built-in CompressionStream("deflate"),
// which produces zlib data = PDF FlateDecode. (Also available in Node 18+.)

const MM_TO_PT = 72 / 25.4;

async function deflate(bytes) {
  const cs = new CompressionStream("deflate");
  const stream = new Blob([bytes]).stream().pipeThrough(cs);
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

const n = (v) => (Math.round(v * 1000) / 1000).toString();

// Rounded rectangle path (PDF coords, origin bottom-left), Bézier corners.
function roundRectPath(x, y, w, h, r) {
  r = Math.max(0, Math.min(r, w / 2, h / 2));
  const k = 0.5522847498 * r;
  return [
    `${n(x + r)} ${n(y)} m`,
    `${n(x + w - r)} ${n(y)} l`,
    `${n(x + w - r + k)} ${n(y)} ${n(x + w)} ${n(y + r - k)} ${n(x + w)} ${n(y + r)} c`,
    `${n(x + w)} ${n(y + h - r)} l`,
    `${n(x + w)} ${n(y + h - r + k)} ${n(x + w - r + k)} ${n(y + h)} ${n(x + w - r)} ${n(y + h)} c`,
    `${n(x + r)} ${n(y + h)} l`,
    `${n(x + r - k)} ${n(y + h)} ${n(x)} ${n(y + h - r + k)} ${n(x)} ${n(y + h - r)} c`,
    `${n(x)} ${n(y + r)} l`,
    `${n(x)} ${n(y + r - k)} ${n(x + r - k)} ${n(y)} ${n(x + r)} ${n(y)} c`,
    "h",
  ].join("\n");
}

function ellipsePath(cx, cy, rx, ry) {
  const kx = 0.5522847498 * rx, ky = 0.5522847498 * ry;
  return [
    `${n(cx + rx)} ${n(cy)} m`,
    `${n(cx + rx)} ${n(cy + ky)} ${n(cx + kx)} ${n(cy + ry)} ${n(cx)} ${n(cy + ry)} c`,
    `${n(cx - kx)} ${n(cy + ry)} ${n(cx - rx)} ${n(cy + ky)} ${n(cx - rx)} ${n(cy)} c`,
    `${n(cx - rx)} ${n(cy - ky)} ${n(cx - kx)} ${n(cy - ry)} ${n(cx)} ${n(cy - ry)} c`,
    `${n(cx + kx)} ${n(cy - ry)} ${n(cx + rx)} ${n(cy - ky)} ${n(cx + rx)} ${n(cy)} c`,
    "h",
  ].join("\n");
}

/**
 * Build the PDF from raw pixels.
 *   pixels: { width, height, data: Uint8ClampedArray (RGBA) } covering
 *           the FULL page (trim + bleed on every side)
 *   widthMm/heightMm: finished (trim) sticker size
 *   bleedMm: artwork overhang beyond the cut line
 *   cut: { type: "roundRect", radiusMm } | { type: "ellipse" }
 * Returns a Blob (application/pdf).
 */
export async function buildCutPdfFromPixels({ pixels, ...rest }) {
  return buildCutSheetFromPixels({ pieces: [{ pixels }], ...rest });
}

/** Polygon cut path: outline [[x,y]…] in mm (y DOWN) inside a trim box
 *  of widthMm × heightMm, placed with its top-left at (x, yTop) in points. */
function polyPath(outline, x, yTop) {
  return outline
    .map(([px, py], i) => `${n(x + px * MM_TO_PT)} ${n(yTop - py * MM_TO_PT)} ${i ? "l" : "m"}`)
    .concat("h")
    .join("\n");
}

/**
 * One PDF page holding several pieces stacked vertically (e.g. shaft +
 * blade stickers, front + back), each with its own artwork and its own
 * CutContour path, so the whole order prints and cuts in a single job.
 *   pieces: [{ pixels: {width,height,data}, widthMm?, heightMm?, cut? }]
 *           (top → bottom). Per-piece size/cut override the defaults.
 *   cut:    { type: "roundRect", radiusMm } | { type: "ellipse" }
 *         | { type: "poly", outline: [[x,y]…] }  (mm, y down, trim box)
 *   gapMm:  space between pieces' bleed edges
 */
export async function buildCutSheetFromPixels({
  pieces,
  widthMm,
  heightMm,
  bleedMm = 2,
  gapMm = 6,
  cut = { type: "roundRect", radiusMm: 3 },
  title = "Print file",
}) {
  const b = bleedMm * MM_TO_PT;
  const gap = gapMm * MM_TO_PT;
  const cells = pieces.map((pc) => {
    const tw = (pc.widthMm ?? widthMm) * MM_TO_PT;
    const th = (pc.heightMm ?? heightMm) * MM_TO_PT;
    return { tw, th, w: tw + b * 2, h: th + b * 2, cut: pc.cut || cut };
  });
  const pageW = Math.max(...cells.map((c) => c.w));
  const pageH = cells.reduce((s, c) => s + c.h, 0) + gap * (cells.length - 1);

  // PDF y is bottom-up: piece 0 sits at the TOP of the page
  const tops = [];
  cells.reduce((y, c) => { tops.push(y); return y - c.h - gap; }, pageH);
  const cellY = (i) => tops[i] - cells[i].h;

  const images = await Promise.all(
    pieces.map(async ({ pixels }) => {
      const { width: iw, height: ih, data } = pixels;
      const rgb = new Uint8Array(iw * ih * 3);
      const alpha = new Uint8Array(iw * ih);
      for (let i = 0, j = 0, k = 0; i < data.length; i += 4) {
        rgb[j++] = data[i];
        rgb[j++] = data[i + 1];
        rgb[j++] = data[i + 2];
        alpha[k++] = data[i + 3];
      }
      const [rgbZ, alphaZ] = await Promise.all([deflate(rgb), deflate(alpha)]);
      return { iw, ih, rgbZ, alphaZ };
    })
  );

  const content = [];
  pieces.forEach((_, i) => {
    const y = cellY(i), c = cells[i];
    content.push("q", `${n(c.w)} 0 0 ${n(c.h)} 0 ${n(y)} cm`, `/Im${i + 1} Do`, "Q");
  });
  // cut lines: spot colour CutContour, hairline, overprint
  content.push("q", "/GS0 gs", "/CS0 CS 1 SCN", "0.25 w");
  pieces.forEach((_, i) => {
    const y = cellY(i), c = cells[i], k = c.cut;
    content.push(
      k.type === "rings" // contour cut: one or more closed outlines
        ? k.rings.map((r) => polyPath(r, b, y + b + c.th)).join("\n")
        : k.type === "poly"
        ? polyPath(k.outline, b, y + b + c.th)
        : k.type === "ellipse"
          ? ellipsePath(b + c.tw / 2, y + b + c.th / 2, c.tw / 2, c.th / 2)
          : roundRectPath(b, y + b, c.tw, c.th, (k.radiusMm ?? 3) * MM_TO_PT),
      "S"
    );
  });
  content.push("Q");

  const enc = new TextEncoder();
  const dict = (s) => enc.encode(s);
  const stream = (header, bytes) => [
    dict(`${header.replace(/>>\s*$/, "")} /Length ${bytes.length} >>\nstream\n`),
    bytes,
    dict("\nendstream"),
  ];

  // 1 Catalog, 2 Pages, 3 Page, 4 Contents, 5 CutContour Separation,
  // 6 tint function, 7 ExtGState, 8 Info, then per piece: image, SMask
  const objs = [];
  const imgObj = (i) => 9 + i * 2;
  const xobjs = pieces.map((_, i) => `/Im${i + 1} ${imgObj(i)} 0 R`).join(" ");
  objs[0] = [dict("<< /Type /Catalog /Pages 2 0 R >>")];
  objs[1] = [dict("<< /Type /Pages /Kids [3 0 R] /Count 1 >>")];
  objs[2] = [
    dict(
      `<< /Type /Page /Parent 2 0 R ` +
        `/MediaBox [0 0 ${n(pageW)} ${n(pageH)}] ` +
        `/BleedBox [0 0 ${n(pageW)} ${n(pageH)}] ` +
        `/TrimBox [${n(b)} ${n(b)} ${n(pageW - b)} ${n(pageH - b)}] ` +
        `/Resources << /XObject << ${xobjs} >> /ColorSpace << /CS0 5 0 R >> /ExtGState << /GS0 7 0 R >> >> ` +
        `/Contents 4 0 R >>`
    ),
  ];
  objs[3] = stream("<< >>", enc.encode(content.join("\n")));
  objs[4] = [dict("[/Separation /CutContour /DeviceCMYK 6 0 R]")];
  objs[5] = [dict("<< /FunctionType 2 /Domain [0 1] /C0 [0 0 0 0] /C1 [0 1 0 0] /N 1 >>")];
  objs[6] = [dict("<< /Type /ExtGState /OP true /op true /OPM 1 >>")];
  const safeTitle = String(title).replace(/[()\\]/g, "");
  objs[7] = [dict(`<< /Title (${safeTitle}) /Producer (Merch Ally engine) >>`)];
  images.forEach(({ iw, ih, rgbZ, alphaZ }, i) => {
    objs[imgObj(i) - 1] = stream(
      `<< /Type /XObject /Subtype /Image /Width ${iw} /Height ${ih} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /FlateDecode /SMask ${imgObj(i) + 1} 0 R >>`,
      rgbZ
    );
    objs[imgObj(i)] = stream(
      `<< /Type /XObject /Subtype /Image /Width ${iw} /Height ${ih} /ColorSpace /DeviceGray /BitsPerComponent 8 /Filter /FlateDecode >>`,
      alphaZ
    );
  });

  // assemble with xref
  const parts = [dict("%PDF-1.6\n%\xE2\xE3\xCF\xD3\n")];
  let offset = parts[0].length;
  const offsets = [];
  objs.forEach((chunks, i) => {
    offsets.push(offset);
    const head = dict(`${i + 1} 0 obj\n`);
    const tail = dict("\nendobj\n");
    parts.push(head, ...chunks, tail);
    offset += head.length + chunks.reduce((s, c) => s + c.length, 0) + tail.length;
  });
  const xrefStart = offset;
  let xref = `xref\n0 ${objs.length + 1}\n0000000000 65535 f \n`;
  for (const o of offsets) xref += `${String(o).padStart(10, "0")} 00000 n \n`;
  xref += `trailer\n<< /Size ${objs.length + 1} /Root 1 0 R /Info 8 0 R >>\nstartxref\n${xrefStart}\n%%EOF\n`;
  parts.push(dict(xref));

  return new Blob(parts, { type: "application/pdf" });
}

/** Convenience: several canvases (trim + bleed each) → one cut sheet.
 *  `pieces` (optional, same order) can carry per-piece widthMm/heightMm/cut. */
export async function buildCutSheet({ canvases, pieces: meta = [], ...rest }) {
  const pieces = canvases.map((c, i) => {
    const img = c.getContext("2d").getImageData(0, 0, c.width, c.height);
    return { ...(meta[i] || {}), pixels: { width: c.width, height: c.height, data: img.data } };
  });
  return buildCutSheetFromPixels({ pieces, ...rest });
}

/** Convenience: build from a canvas that covers trim + bleed. */
export async function buildCutPdf({ canvas, ...rest }) {
  const ctx = canvas.getContext("2d");
  const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
  return buildCutPdfFromPixels({
    pixels: { width: canvas.width, height: canvas.height, data: img.data },
    ...rest,
  });
}
